import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { RouterHistoryService } from './core/services/router-history.service';

// Keeps a single log line bounded when an error carries a huge payload.
const MAX_SERIALIZED_LENGTH = 4000;

// A chunk request fails after a deployment when a tab still runs the previous bundle. Each browser words it differently.
const CHUNK_LOAD_FAILURE_PATTERN = new RegExp(
  [
    // webpack
    'Loading chunk [^ ]+ failed',
    // Safari
    'Importing a module script failed',
    // Firefox
    'error loading dynamically imported module',
    // Chrome and Edge (a TypeError)
    'Failed to fetch dynamically imported module',
  ].join('|'),
);

// Session key holding the time of the last reload triggered by a chunk load failure.
const CHUNK_LOAD_FAILURE_RELOAD_AT_KEY = 'chunkLoadFailureReloadAt';

// A second chunk failure within this delay of a reload means the reload did not fix it: stop reloading.
const CHUNK_LOAD_FAILURE_RELOAD_GUARD_MS = 30_000;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private routerHistoryService = inject(RouterHistoryService);

  public handleError(error: unknown): void {
    if (error instanceof Error && CHUNK_LOAD_FAILURE_PATTERN.test(error.message)) {
      this._reloadAfterChunkLoadFailure(error);
      return;
    }

    if (error instanceof HttpErrorResponse) {
      if (
        error.status === 404 ||
        error.status === 403 ||
        error.status === 401 ||
        error.status === 400 ||
        error.status === -1 ||
        error.status === -2
      ) {
        console.log(error);
        return;
      }
    }

    if (error instanceof Error && !error.message) {
      // Datadog builds the log message from "name: message"; an empty message yields the useless
      // "Error:" pattern with no attribute at all (Safari notably, see the production-flow pages).
      // Serialize whatever the error carries so the pattern stays diagnosable.
      console.error(`Unhandled error without message ${this._serializeErrorDetails(error)}`, error);
      return;
    }

    console.error(error);
  }

  private _reloadAfterChunkLoadFailure(error: Error): void {
    try {
      const now = Date.now();
      const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_LOAD_FAILURE_RELOAD_AT_KEY));

      if (lastReloadAt && now - lastReloadAt < CHUNK_LOAD_FAILURE_RELOAD_GUARD_MS) {
        console.error(`Chunk load failed again right after a reload, giving up the reload to avoid a reload loop: ${error.message}`, error);
        return;
      }

      window.sessionStorage.setItem(CHUNK_LOAD_FAILURE_RELOAD_AT_KEY, String(now));
    } catch (storageError) {
      console.error(
        `Chunk load failed but sessionStorage is unavailable, giving up the reload because a reload loop could not be prevented: ${error.message}`,
        storageError,
      );
      return;
    }

    console.log(`Chunk load failed, reloading the application to : ${this.routerHistoryService.navigationStartUrl}`);
    window.location.href = this.routerHistoryService.navigationStartUrl;
  }

  private _serializeErrorDetails(error: Error): string {
    const details: Record<string, unknown> = { name: error.name, stack: error.stack };

    // Own properties carry the payload of wrapped errors: "cause" (ES2022), "rejection"
    // (zone.js unhandled promise rejections) or any custom field attached by a library.
    for (const key of Object.getOwnPropertyNames(error)) {
      if (key !== 'message' && !(key in details)) {
        details[key] = (error as unknown as Record<string, unknown>)[key];
      }
    }

    // Objects already visited: a repeated reference is replaced, which breaks cycles such as the
    // parent <-> children structure of the router's UrlSegmentGroup (carried by its NoMatch error).
    const seen = new WeakSet<object>();

    try {
      const serialized = JSON.stringify(details, (_key, value: unknown) => {
        if (value instanceof Error) {
          return `${value.name}: ${value.message} ${value.stack ?? ''}`.trim();
        }
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular]';
          }
          seen.add(value);
        }
        return value;
      });

      return serialized.length > MAX_SERIALIZED_LENGTH ? `${serialized.slice(0, MAX_SERIALIZED_LENGTH)}…(truncated)` : serialized;
    } catch {
      // Non-serializable payload (e.g. BigInt): fall back to the minimum viable detail.
      return `{"name":"${error.name}"}`;
    }
  }
}
